import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Grid, Table, TableBody, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import { withRights } from 'providers/RightsConsumer';
import { RightsContext } from 'providers/RightsContext';
import { IconButton, Button } from '@totalsoft/rocket-ui';
import { DenseTableCell, StickyTableFooter } from 'components/common/CommonStyles';
import { PaymentPlanTableRow } from './OfferPaymentPlanStyles';
import { Upload } from '@mui/icons-material';
import excelIcon from 'assets/img/microsoft-excel-icon.svg';
import pdfIcon from 'assets/img/pdf-icon.svg';
import wordIcon from 'assets/img/microsoft-word-icon.svg';
import { BoxActionsWrapper } from 'components/common/CommonStyles';

const OfferPaymentPlanComponent = React.memo(
  ({
    paymentPlan,
    getLabelValue,
    importPaymentPlan,
    allowScheduleImport,
    rights,
    showScheduleImportButton,
    exportPaymentPlan,
    fileInputRef,
    onImportScheduleClick,
    exportPaymentPlanWithTemplateOutput,
    isInvoiceGenerationVisible,
    canGenerateInvoice,
    generateInvoice,
    canDownloadInvoice,
    isDownloadingInvoice,
    downloadInvoice,
    cancelInvoice,
    canCancelInvoice,
    cancellingInvoice
  }) => {
    const { isImage } = useContext(RightsContext);
    const { t } = useTranslation();

    return (
      <Box sx={{ overflowX: 'auto' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {paymentPlan.labels?.map(n => {
                  return (
                    n.visible && (
                      <Tooltip usePortal={false} title={n.caption} enterDelay={500} leaveDelay={200} key={n.name}>
                        <DenseTableCell key={n.name}>
                          <strong>{n.caption}</strong>
                        </DenseTableCell>
                      </Tooltip>
                    )
                  );
                })}
              </TableRow>
            </TableHead>
            {paymentPlan.values?.length > 0 && (
              <TableBody>
                {paymentPlan.values.map((n, index) => {
                  return (
                    <PaymentPlanTableRow key={index} invoiced={n.Invoiced}>
                      {paymentPlan.labels.map(m => {
                        return (
                          m.visible && (
                            <DenseTableCell key={m.name}>{getLabelValue(n[m.name], m.systemTypeName, m.displayFormat)}</DenseTableCell>
                          )
                        );
                      })}
                    </PaymentPlanTableRow>
                  );
                })}
              </TableBody>
            )}
            <StickyTableFooter>
              <TableRow>
                {paymentPlan.labels.map((m, index) => {
                  return (
                    m.visible && (
                      <DenseTableCell key={m.name}>
                        {index === 0 ? (
                          <strong>{'Total'}</strong>
                        ) : m.summarizeYN ? (
                          <strong>
                            {getLabelValue(
                              paymentPlan.values.map(v => v[m.name]).reduce((prev, curr) => prev + curr, 0),
                              m.systemTypeName,
                              m.displayFormat
                            )}
                          </strong>
                        ) : (
                          ''
                        )}
                      </DenseTableCell>
                    )
                  );
                })}
              </TableRow>
            </StickyTableFooter>
          </Table>
        </TableContainer>
        <Grid container justifyContent='space-between'>
          {isInvoiceGenerationVisible ? (
            <Grid item>
              <BoxActionsWrapper mt={1}>
                {rights.generateDownpaymentInvoice && (
                  <Button
                    id={'offerPaymentPlanCard_generateInvoiceBtn'}
                    disabled={rights.readOnlyFull || !paymentPlan || !canGenerateInvoice}
                    onClick={generateInvoice}
                  >
                    {t('OfferEdit.FinancialServices.GenerateInvoice')}
                  </Button>
                )}
                {rights.cancelDownpaymentInvoice && (
                  <Button
                    id={'offerPaymentPlanCard_cancelInvoiceBtn'}
                    disabled={rights.readOnlyFull || !paymentPlan || cancellingInvoice || !canCancelInvoice}
                    onClick={cancelInvoice}
                  >
                    {t('OfferEdit.FinancialServices.CancelInvoice')}
                  </Button>
                )}
                {rights.generateDownpaymentInvoice && (
                  <Button
                    id={'offerPaymentPlanCard_downloadInvoiceBtn'}
                    disabled={rights.readOnlyFull || !paymentPlan || isDownloadingInvoice || !canDownloadInvoice}
                    onClick={downloadInvoice}
                  >
                    {t('OfferEdit.FinancialServices.PrintInvoice')}
                  </Button>
                )}
              </BoxActionsWrapper>
            </Grid>
          ) : (
            <Grid item> </Grid>
          )}

          {!paymentPlan.hidden && (
            <Grid item>
              <Grid container justifyContent='flex-end' gap={1} mb={2}>
                <Grid>
                  {!isImage && (
                    <div>
                      <input
                        style={{ display: 'none' }}
                        value={''}
                        type='file'
                        id='selectFile'
                        multiple
                        ref={fileInputRef}
                        accept='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
                        onChange={event => importPaymentPlan(event.target.files[0])}
                        disabled={!rights.importSchedule || rights.readOnlyFull}
                      />
                      <label htmlFor='selectFile'>
                        {allowScheduleImport && showScheduleImportButton && (
                          <IconButton
                            id={'offerPaymentPlanCard_importBtn'}
                            disabled={!rights.importSchedule || rights.readOnlyFull}
                            onClick={onImportScheduleClick}
                            tooltip={t('OfferEdit.FinancialServices.Import')}
                            variant='text'
                          >
                            <Upload style={{ color: 'green' }} />
                          </IconButton>
                        )}
                      </label>
                    </div>
                  )}
                </Grid>
                <Grid>
                  <label htmlFor='exportFile'>
                    <IconButton
                      id={'offerPaymentPlanCard_exportBtn'}
                      disabled={rights.readOnlyFull || !paymentPlan}
                      onClick={() => exportPaymentPlan('excel')}
                      tooltip={t('OfferEdit.FinancialServices.Export', {
                        data: { exportType: 'excel' }
                      })}
                      variant='text'
                    >
                      <img src={excelIcon} />
                    </IconButton>
                  </label>
                </Grid>

                {exportPaymentPlanWithTemplateOutput && (
                  <Grid>
                    <label htmlFor='exportFile'>
                      <IconButton
                        id={'offerPaymentPlanCard_exportBtn'}
                        disabled={rights.readOnlyFull || !paymentPlan}
                        onClick={() => exportPaymentPlan(exportPaymentPlanWithTemplateOutput)}
                        tooltip={t('OfferEdit.FinancialServices.Export', {
                          data: { exportType: exportPaymentPlanWithTemplateOutput }
                        })}
                        variant='text'
                      >
                        <img src={exportPaymentPlanWithTemplateOutput === 'pdf' ? pdfIcon : wordIcon} />
                      </IconButton>
                    </label>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  }
);

OfferPaymentPlanComponent.displayName = 'OfferPaymentPlanComponent';

OfferPaymentPlanComponent.propTypes = {
  paymentPlan: PropTypes.object.isRequired,
  getLabelValue: PropTypes.func.isRequired,
  importPaymentPlan: PropTypes.func.isRequired,
  allowScheduleImport: PropTypes.bool.isRequired,
  rights: PropTypes.object.isRequired,
  showScheduleImportButton: PropTypes.bool.isRequired,
  exportPaymentPlan: PropTypes.func.isRequired,
  onImportScheduleClick: PropTypes.func.isRequired,
  fileInputRef: PropTypes.object,
  exportPaymentPlanWithTemplateOutput: PropTypes.string,
  isInvoiceGenerationVisible: PropTypes.bool.isRequired,
  canGenerateInvoice: PropTypes.bool.isRequired,
  canDownloadInvoice: PropTypes.bool.isRequired,
  generateInvoice: PropTypes.func.isRequired,
  isDownloadingInvoice: PropTypes.bool.isRequired,
  downloadInvoice: PropTypes.func.isRequired,
  cancelInvoice: PropTypes.func.isRequired,
  canCancelInvoice: PropTypes.bool.isRequired,
  cancellingInvoice: PropTypes.bool.isRequired
};

export default withRights(OfferPaymentPlanComponent);
